import { useEffect, useRef, useState } from "react";

export interface SelectOption {
  value: string;
  label: string;
  icon?: string;
}

interface SelectProps {
  value: string;
  options: SelectOption[];
  onChange: (value: string) => void;
  placeholder?: string;
  ariaLabel?: string;
  className?: string;
  align?: "start" | "end";
}

export default function Select({
  value,
  options,
  onChange,
  placeholder = "اختر...",
  ariaLabel,
  className = "",
  align,
}: SelectProps) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  const selected = options.find((o) => o.value === value);
  const panelAlign = align ?? "start";

  useEffect(() => {
    if (!open) return;
    const handleClick = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", handleClick);
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("mousedown", handleClick);
      document.removeEventListener("keydown", handleKey);
    };
  }, [open]);

  const handleSelect = (next: string) => {
    onChange(next);
    setOpen(false);
  };

  return (
    <div ref={rootRef} className={`relative ${className}`}>
      <button
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={ariaLabel}
        onClick={() => setOpen((v) => !v)}
        className={`w-full h-11 rounded-btn border bg-background-50 px-3.5 flex items-center justify-between gap-2 text-sm transition-colors cursor-pointer ${
          open
            ? "border-primary-500 ring-2 ring-primary-100"
            : "border-background-300 hover:border-background-400"
        }`}
      >
        <span className="flex items-center gap-2 min-w-0">
          {selected?.icon ? (
            <i className={`${selected.icon} text-base text-foreground-500 shrink-0`}></i>
          ) : null}
          <span
            className={`truncate ${
              selected ? "text-foreground-800 font-semibold" : "text-foreground-400"
            }`}
          >
            {selected ? selected.label : placeholder}
          </span>
        </span>
        <i
          className={`ri-arrow-down-s-line text-lg text-foreground-400 shrink-0 transition-transform duration-200 ${
            open ? "rotate-180" : ""
          }`}
        ></i>
      </button>

      {open ? (
        <div
          role="listbox"
          className={`absolute z-40 mt-1.5 min-w-full w-max max-w-[16rem] rounded-card border border-background-200 bg-background-50 shadow-dropdown overflow-hidden animate-fade-slide-down ${
            panelAlign === "end" ? "left-0" : "right-0"
          }`}
        >
          <div className="max-h-64 overflow-y-auto py-1">
            {options.map((option) => {
              const active = option.value === value;
              return (
                <button
                  key={option.value}
                  type="button"
                  role="option"
                  aria-selected={active}
                  onClick={() => handleSelect(option.value)}
                  className={`w-full flex items-center justify-between gap-3 px-3.5 py-2.5 text-sm text-right transition-colors cursor-pointer ${
                    active
                      ? "bg-primary-50 text-primary-700 font-bold"
                      : "text-foreground-700 hover:bg-background-100"
                  }`}
                >
                  <span className="flex items-center gap-2 min-w-0">
                    {option.icon ? (
                      <i
                        className={`${option.icon} text-base shrink-0 ${
                          active ? "text-primary-600" : "text-foreground-400"
                        }`}
                      ></i>
                    ) : null}
                    <span className="truncate">{option.label}</span>
                  </span>
                  {active ? (
                    <i className="ri-check-line text-base text-primary-600 shrink-0"></i>
                  ) : null}
                </button>
              );
            })}
          </div>
        </div>
      ) : null}
    </div>
  );
}